import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  collection, doc, getDocs, getDoc, setDoc, deleteDoc, updateDoc,
  query, orderBy, serverTimestamp, where,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { computeScore } from '@/lib/scoring'
import type { AppRecord } from '@/lib/types'

export const APPS_KEY = ['apps'] as const

export function useApps() {
  return useQuery({
    queryKey: APPS_KEY,
    queryFn: async (): Promise<AppRecord[]> => {
      const q = query(collection(db, 'apps'), orderBy('last_modified', 'desc'))
      const snap = await getDocs(q)
      return snap.docs.map((d) => {
        const data = d.data() as AppRecord
        const enriched = { ...data, id: d.id }
        if (!enriched.score) enriched.score = computeScore(enriched)
        return enriched
      })
    },
  })
}

export function useApp(slug: string | undefined) {
  return useQuery({
    queryKey: ['apps', slug],
    enabled: !!slug,
    queryFn: async (): Promise<AppRecord | null> => {
      if (!slug) return null
      const q = query(collection(db, 'apps'), where('slug', '==', slug))
      const snap = await getDocs(q)
      if (snap.empty) return null
      const d = snap.docs[0]
      const data = d.data() as AppRecord
      return { ...data, id: d.id, score: data.score ?? computeScore(data) }
    },
  })
}

export function useUpsertApp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: AppRecord) => {
      const ref = input.id ? doc(db, 'apps', input.id) : doc(collection(db, 'apps'))
      const payload = {
        ...input,
        score: computeScore(input),
        updated_at: serverTimestamp(),
        ...(input.id ? {} : { created_at: serverTimestamp() }),
      }
      await setDoc(ref, payload, { merge: true })
      return { ...payload, id: ref.id }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: APPS_KEY })
    },
  })
}

export function useDeleteApp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, 'apps', id))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: APPS_KEY }),
  })
}

export function usePatchApp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<AppRecord> }) => {
      await updateDoc(doc(db, 'apps', id), { ...patch, updated_at: serverTimestamp() })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: APPS_KEY }),
  })
}

export async function getAppById(id: string): Promise<AppRecord | null> {
  const snap = await getDoc(doc(db, 'apps', id))
  if (!snap.exists()) return null
  return { ...(snap.data() as AppRecord), id: snap.id }
}
